import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button } from '@mono/ui-components';
import { AlertTriangle, ChevronLeft, RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChatView } from '../../components/ChatView';
import { ChatHistoryUnavailable } from '../../components/ChatView/ChatRecovery';
import { PageBody, PageSectionErrorBoundary } from '../../components/layout';
import { PageHeader } from '../../components/PageHeader';
import { useProjectContext } from '../../contexts/ProjectContext';
import { useChatHistory } from '../../hooks/useConversation';
import { useLayoutStore } from '../../lib/stores/LayoutStore';
function ChatErrorFallback() {
    return /*#__PURE__*/ _jsx("div", {
        className: "flex-1 flex items-center justify-center",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "text-center p-6",
            children: [
                /*#__PURE__*/ _jsx(AlertTriangle, {
                    className: "h-10 w-10 text-amber-500 mx-auto mb-3"
                }),
                /*#__PURE__*/ _jsx("h2", {
                    className: "text-lg font-semibold mb-2",
                    children: "Something went wrong"
                }),
                /*#__PURE__*/ _jsx("p", {
                    className: "text-sm text-muted-foreground mb-4",
                    children: "The chat encountered an error. Please try refreshing the page."
                }),
                /*#__PURE__*/ _jsxs(Button, {
                    variant: "outline",
                    onClick: ()=>window.location.reload(),
                    children: [
                        /*#__PURE__*/ _jsx(RefreshCw, {
                            className: "w-4 h-4 mr-2"
                        }),
                        "Refresh page"
                    ]
                })
            ]
        })
    });
}
/**
 * Project-scoped chat view.
 * Similar to the Chat page but with project context and proper breadcrumbs.
 */ export function ProjectChatView() {
    const navigate = useNavigate();
    const { chatId } = useParams();
    const { projectId, project, canEdit, isLoading: projectLoading, error: projectError } = useProjectContext();
    const setShowBreadcrumbs = useLayoutStore((state)=>state.setShowBreadcrumbs);
    // Hide breadcrumbs on chat page
    useEffect(()=>{
        setShowBreadcrumbs(false);
        return ()=>setShowBreadcrumbs(true);
    }, [
        setShowBreadcrumbs
    ]);
    const [newChatId] = useState(()=>chatId && sessionStorage.getItem(`init::${chatId}`) !== null ? chatId : undefined);
    const [acknowledgedChatId, setAcknowledgedChatId] = useState();
    const startingNewChat = !!chatId && chatId === newChatId && chatId !== acknowledgedChatId;
    const history = useChatHistory(chatId, startingNewChat);
    const { data, isLoading: chatLoading } = history;
    const initialMessages = data?.messages ?? [];
    if (projectLoading) {
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(PageHeader, {
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "pl-8",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "h-4 w-24 bg-muted rounded animate-pulse mb-2"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "h-6 w-32 bg-muted rounded animate-pulse"
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(PageBody, {
                    children: /*#__PURE__*/ _jsx("div", {
                        className: "h-96 bg-muted rounded-lg animate-pulse",
                        "aria-busy": "true"
                    })
                })
            ]
        });
    }
    if (projectError || !project || !projectId) {
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(PageHeader, {
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "pl-8",
                        children: [
                            /*#__PURE__*/ _jsxs(Link, {
                                to: "/projects",
                                className: "text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-1",
                                children: [
                                    /*#__PURE__*/ _jsx(ChevronLeft, {
                                        className: "w-4 h-4"
                                    }),
                                    "Projects"
                                ]
                            }),
                            /*#__PURE__*/ _jsx("h2", {
                                className: "text-xl font-semibold",
                                children: "Project not found"
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(PageBody, {
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "text-center py-12",
                        children: [
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-red-600 mb-4",
                                children: "This project doesn't exist or you don't have access."
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                variant: "outline",
                                onClick: ()=>navigate('/projects'),
                                children: "Back to Projects"
                            })
                        ]
                    })
                })
            ]
        });
    }
    if (!chatId) {
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(PageHeader, {
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "pl-8",
                        children: [
                            /*#__PURE__*/ _jsxs(Link, {
                                to: `/project/${projectId}/chat`,
                                className: "text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-1",
                                children: [
                                    /*#__PURE__*/ _jsx(ChevronLeft, {
                                        className: "w-4 h-4"
                                    }),
                                    "Chats"
                                ]
                            }),
                            /*#__PURE__*/ _jsx("h2", {
                                className: "text-xl font-semibold",
                                children: "Chat not found"
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(PageBody, {
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "text-center py-12",
                        children: [
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-muted-foreground mb-4",
                                children: "No chat ID provided."
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                variant: "outline",
                                onClick: ()=>navigate(`/project/${projectId}/chat`),
                                children: "Back to Chats"
                            })
                        ]
                    })
                })
            ]
        });
    }
    if (chatLoading && chatId !== newChatId) {
        return /*#__PURE__*/ _jsx("div", {
            className: "flex items-center justify-center h-full",
            children: /*#__PURE__*/ _jsx("div", {
                className: "animate-spin rounded-full h-8 w-8 border-b-2 border-primary"
            })
        });
    }
    if (history.isError) {
        return /*#__PURE__*/ _jsx(ChatHistoryUnavailable, {
            error: history.error,
            onRetry: ()=>void history.refetch()
        });
    }
    return /*#__PURE__*/ _jsx(PageSectionErrorBoundary, {
        sectionName: "Chat",
        fallback: /*#__PURE__*/ _jsx(ChatErrorFallback, {}),
        children: /*#__PURE__*/ _jsx(ChatView, {
            chatId: chatId,
            projectId: projectId,
            initialMessages: initialMessages,
            readOnly: !canEdit || !!data?.readOnly,
            variant: "specra",
            onConversationCreated: ()=>setAcknowledgedChatId(chatId)
        })
    });
}
